/**
 * Inspections.
 *
 * An inspection is a record of the condition of somewhere, room by room, on one day. The office
 * cares about exactly two things it produces, and both are here rather than in a screen:
 *
 * 1. **What changed since the last time.** A list of conditions on its own is an opinion; the same
 *    list set against the previous visit is evidence, and evidence is what a deposit deduction or
 *    a landlord's repair bill actually rests on. `compareInspections` is the whole of that, and it
 *    is a pure function so the browser, the API and the printed report cannot disagree about it.
 * 2. **What has to be done about it.** A finding that has to be retyped into the maintenance log
 *    is a finding that will not be. `maintenanceDraftFrom` decides once what a job raised from a
 *    finding says and how urgent it is, so the button on the review screen and any future
 *    automation raise the same job.
 *
 * **A condition is ranked, and the ranking is the point.** "Fair" to "poor" is a deterioration and
 * "poor" to "fair" is a repair; without an order the comparison can only say "different", which
 * tells a manager nothing they could not see. `not_checked` deliberately has no rank — a room
 * nobody entered is not an improvement on a damaged one, and pretending otherwise would put a
 * false "better" on a report somebody may be shown in a dispute.
 */

import type { PlainDate } from "./dates";
import type { MaintenancePriority } from "./portfolio";

/* ---------------------------------------------------------------- what it is */

export type InspectionKind = "move_in" | "routine" | "mid_term" | "move_out";

export const INSPECTION_KINDS: Array<{
  value: InspectionKind;
  label: string;
  /** Shown beside the choice, because the four are not interchangeable on a report. */
  description: string;
}> = [
  {
    value: "move_in",
    label: "Check-in",
    description: "The condition a tenancy starts from. Everything later is measured against it.",
  },
  {
    value: "routine",
    label: "Routine",
    description: "A periodic visit during a tenancy, to catch a problem before it becomes a bill.",
  },
  {
    value: "mid_term",
    label: "Mid-term",
    description: "A formal review part-way through a long tenancy, usually before a renewal.",
  },
  {
    value: "move_out",
    label: "Check-out",
    description: "The condition a tenancy ends in. This is the one a deposit deduction rests on.",
  },
];

export type InspectionStatus = "scheduled" | "in_progress" | "completed" | "cancelled";

export const INSPECTION_STATUSES: Array<{
  value: InspectionStatus;
  label: string;
  /** Still owed to somebody. What the "due" filter and the count on the list mean. */
  open: boolean;
  tone: "info" | "warn" | "success" | "muted";
}> = [
  { value: "scheduled", label: "Scheduled", open: true, tone: "info" },
  { value: "in_progress", label: "In progress", open: true, tone: "warn" },
  { value: "completed", label: "Completed", open: false, tone: "success" },
  { value: "cancelled", label: "Cancelled", open: false, tone: "muted" },
];

export const INSPECTION_KIND_VALUES = INSPECTION_KINDS.map((kind) => kind.value);
export const INSPECTION_STATUS_VALUES = INSPECTION_STATUSES.map((status) => status.value);

export function inspectionKindLabel(kind: string | null | undefined): string {
  return INSPECTION_KINDS.find((entry) => entry.value === kind)?.label ?? "Inspection";
}

export function inspectionStatusLabel(status: string | null | undefined): string {
  return INSPECTION_STATUSES.find((entry) => entry.value === status)?.label ?? "Scheduled";
}

/* ------------------------------------------------------------------ condition */

export type Condition = "good" | "fair" | "poor" | "damaged" | "missing" | "not_checked";

export interface ConditionInfo {
  value: Condition;
  label: string;
  /**
   * Where this sits on the scale from sound to gone. `null` for `not_checked`, which is an absence
   * of information rather than a position on it — see the note at the top of the file.
   */
  rank: number | null;
  /** True when this is something somebody has to act on rather than merely note. */
  finding: boolean;
  tone: "success" | "info" | "warn" | "danger" | "muted";
  /** A worked example, so two inspectors grade the same thing the same way. */
  help: string;
}

export const CONDITIONS: ConditionInfo[] = [
  {
    value: "good",
    label: "Good",
    rank: 0,
    finding: false,
    tone: "success",
    help: "Sound and working. A newly painted wall, a tap that runs and does not drip.",
  },
  {
    value: "fair",
    label: "Fair",
    rank: 1,
    finding: false,
    tone: "info",
    help: "Serviceable, with wear that is reasonable for its age. Scuffed paint, a worn but whole worktop.",
  },
  {
    value: "poor",
    label: "Poor",
    rank: 2,
    finding: true,
    tone: "warn",
    help: "Still usable but needs attention before it fails. A dripping tap, a door that binds.",
  },
  {
    value: "damaged",
    label: "Damaged",
    rank: 3,
    finding: true,
    tone: "danger",
    help: "Broken, unsafe, or not usable for its purpose. A cracked pane, a socket hanging off the wall.",
  },
  {
    value: "missing",
    label: "Missing",
    rank: 4,
    finding: true,
    tone: "danger",
    help: "It is not there. A removed fitting, a key not handed back, an appliance taken away.",
  },
  {
    value: "not_checked",
    label: "Not checked",
    rank: null,
    finding: false,
    tone: "muted",
    help: "Not seen on this visit — a locked room, an occupier who was out. Deliberately not a grade.",
  },
];

export const CONDITION_VALUES = CONDITIONS.map((entry) => entry.value);

export function conditionInfo(condition: string | null | undefined): ConditionInfo {
  return CONDITIONS.find((entry) => entry.value === condition) ?? CONDITIONS[CONDITIONS.length - 1];
}

export function conditionRank(condition: string | null | undefined): number | null {
  return conditionInfo(condition).rank;
}

/** Does this need somebody to do something? Poor, damaged and missing do; the rest do not. */
export function isFinding(condition: string | null | undefined): boolean {
  return conditionInfo(condition).finding;
}

/* ------------------------------------------------------------- the checklist */

/**
 * What an inspector is handed when an inspection is scheduled.
 *
 * **A blank sheet is why inspections do not get done.** Somebody standing in a hallway with a
 * phone will not type out thirty lines, so an inspection created from nothing gets three lines and
 * a report nobody can compare against anything. These are seeded at `not_checked`, which is
 * honest: the row exists, and until somebody grades it the report says so rather than implying
 * the place was found in good order.
 *
 * The list is deliberately short and generic — every dwelling has these, and an agency that wants
 * more adds them on the day. It is not a template system, and should not become one until an
 * agency asks for it.
 */
export const DEFAULT_CHECKLIST: Array<{ area: string; item: string }> = [
  { area: "Entrance", item: "Front door, frame and locks" },
  { area: "Entrance", item: "Keys handed over" },
  { area: "Living room", item: "Walls and ceiling" },
  { area: "Living room", item: "Floor covering" },
  { area: "Living room", item: "Windows and blinds" },
  { area: "Kitchen", item: "Sink, taps and drainage" },
  { area: "Kitchen", item: "Worktops and cupboards" },
  { area: "Kitchen", item: "Cooker and extractor" },
  { area: "Bedroom", item: "Walls, ceiling and floor" },
  { area: "Bedroom", item: "Wardrobes and fittings" },
  { area: "Bathroom", item: "Toilet, cistern and seat" },
  { area: "Bathroom", item: "Shower, bath and taps" },
  { area: "Bathroom", item: "Tiling and sealant" },
  { area: "Electrical", item: "Sockets, switches and light fittings" },
  { area: "Electrical", item: "Distribution board and meter reading" },
  { area: "Plumbing", item: "Water storage and pressure" },
  { area: "Security", item: "Gates, burglar bars and alarm" },
  { area: "Exterior", item: "Roof, gutters and external walls" },
  { area: "Exterior", item: "Compound, drainage and refuse" },
];

/** The areas offered when somebody adds a line of their own. Free text is still accepted. */
export const INSPECTION_AREAS = [
  "Entrance",
  "Living room",
  "Dining room",
  "Kitchen",
  "Bedroom",
  "Bathroom",
  "Toilet",
  "Balcony",
  "Store",
  "Electrical",
  "Plumbing",
  "Security",
  "Exterior",
  "Common areas",
  "General",
] as const;

/* ------------------------------------------------------------- what it found */

export interface InspectionItemLike {
  area: string;
  item: string;
  condition?: string | null;
  deductionAmount?: number | null;
}

export interface InspectionSummary {
  /** How many lines the inspection has. */
  total: number;
  /** How many of them somebody has actually graded. */
  graded: number;
  /** Poor, damaged or missing — the ones that lead somewhere. */
  findings: number;
  byCondition: Record<Condition, number>;
  /** Minor units, an integer. Never a float — ADR-0003. */
  deductions: number;
}

export function summariseInspection(items: readonly InspectionItemLike[]): InspectionSummary {
  const byCondition = Object.fromEntries(CONDITIONS.map((entry) => [entry.value, 0])) as Record<Condition, number>;
  let findings = 0;
  let graded = 0;
  let deductions = 0;

  for (const item of items) {
    const info = conditionInfo(item.condition);
    byCondition[info.value] += 1;
    if (info.rank !== null) graded += 1;
    if (info.finding) findings += 1;
    // Integer arithmetic only. A deduction arrives as a count of minor units and stays one.
    deductions += Math.round(Number(item.deductionAmount ?? 0));
  }

  return { total: items.length, graded, findings, byCondition, deductions };
}

/* ----------------------------------------------------------- what has changed */

export type ChangeDirection = "worse" | "better" | "unchanged" | "unknown" | "new" | "gone";

export interface InspectionChange {
  area: string;
  item: string;
  /** What it was last time, or null when the line is new. */
  from: Condition | null;
  /** What it is now, or null when the line was dropped. */
  to: Condition | null;
  direction: ChangeDirection;
  /** How many steps down the scale it moved. Always positive; zero unless the direction is worse. */
  steps: number;
}

/**
 * One line matched against another.
 *
 * Matching is on area **and** item, case- and space-insensitively, because the two inspections were
 * typed months apart by two different people and "Kitchen / Sink, taps and drainage" must find
 * "kitchen / Sink,  taps and drainage". Matching on the item alone would collide every "Walls and
 * ceiling" in the building into one row.
 */
const keyOf = (item: { area: string; item: string }) =>
  `${item.area.trim().toLowerCase().replace(/\s+/g, " ")} ${item.item.trim().toLowerCase().replace(/\s+/g, " ")}`;

/**
 * What changed between two inspections of the same place.
 *
 * **This is the reason an inspection is worth doing at all.** A manager is not asking what
 * condition the flat is in; they are asking what the tenant did to it since the last visit, and
 * that is a difference, not a state.
 *
 * Rows are returned in a deliberate order — deteriorations first, then things that appeared, then
 * repairs, then everything else — because a report is read from the top and the top is where the
 * argument is. Within a group the order is the current inspection's own, so the report reads down
 * the building the way the inspector walked it.
 */
export function compareInspections(
  previous: readonly InspectionItemLike[],
  current: readonly InspectionItemLike[],
): InspectionChange[] {
  const before = new Map(previous.map((item) => [keyOf(item), item]));
  const changes: InspectionChange[] = [];

  for (const item of current) {
    const was = before.get(keyOf(item));
    const to = conditionInfo(item.condition);

    if (!was) {
      changes.push({ area: item.area, item: item.item, from: null, to: to.value, direction: "new", steps: 0 });
      continue;
    }

    const from = conditionInfo(was.condition);
    before.delete(keyOf(item));

    // Either side ungraded means there is nothing to compare. Saying "unchanged" there would put a
    // reassurance on a report that nobody actually checked.
    if (from.rank === null || to.rank === null) {
      changes.push({ area: item.area, item: item.item, from: from.value, to: to.value, direction: "unknown", steps: 0 });
      continue;
    }

    const delta = to.rank - from.rank;
    changes.push({
      area: item.area,
      item: item.item,
      from: from.value,
      to: to.value,
      direction: delta > 0 ? "worse" : delta < 0 ? "better" : "unchanged",
      steps: delta > 0 ? delta : 0,
    });
  }

  // Anything the previous inspection listed and this one does not. Not a fault, but the report has
  // to say so — an item that quietly stops being inspected is how a problem disappears from view.
  for (const left of before.values()) {
    changes.push({
      area: left.area,
      item: left.item,
      from: conditionInfo(left.condition).value,
      to: null,
      direction: "gone",
      steps: 0,
    });
  }

  const weight: Record<ChangeDirection, number> = { worse: 0, new: 1, better: 2, unknown: 3, gone: 4, unchanged: 5 };
  return changes
    .map((change, index) => ({ change, index }))
    /* A total order. Sorting on the direction alone leaves rows of equal weight in whatever order
       the engine felt like, and a report that reshuffles itself between two loads is a report
       somebody stops trusting — the same trap the invoice list fell into. */
    .sort((a, b) =>
      weight[a.change.direction] - weight[b.change.direction] ||
      b.change.steps - a.change.steps ||
      a.index - b.index)
    .map((entry) => entry.change);
}

export interface ComparisonSummary {
  worse: number;
  better: number;
  unchanged: number;
  unknown: number;
  added: number;
  removed: number;
}

export function summariseComparison(changes: readonly InspectionChange[]): ComparisonSummary {
  const summary: ComparisonSummary = { worse: 0, better: 0, unchanged: 0, unknown: 0, added: 0, removed: 0 };
  for (const change of changes) {
    if (change.direction === "new") summary.added += 1;
    else if (change.direction === "gone") summary.removed += 1;
    else summary[change.direction] += 1;
  }
  return summary;
}

/** One sentence for the top of a report. Deliberately plain; a report is not the place for tone. */
export function describeComparison(summary: ComparisonSummary): string {
  if (summary.worse === 0 && summary.better === 0 && summary.added === 0) {
    return "Nothing has changed since the previous inspection.";
  }
  const parts: string[] = [];
  if (summary.worse) parts.push(`${summary.worse} ${summary.worse === 1 ? "item has" : "items have"} deteriorated`);
  if (summary.better) parts.push(`${summary.better} ${summary.better === 1 ? "has" : "have"} improved`);
  if (summary.added) parts.push(`${summary.added} ${summary.added === 1 ? "is new" : "are new"}`);
  if (summary.removed) parts.push(`${summary.removed} ${summary.removed === 1 ? "was" : "were"} not inspected this time`);
  return `${parts.join(", ")}.`;
}

/* --------------------------------------------------- from a finding to a job */

/**
 * How urgent a job raised from a finding starts out.
 *
 * **Emergency is never suggested.** It is four hours on the SLA clock and it wakes people up; a
 * grade typed into a checklist is not enough to commit somebody's night to, so the ladder stops at
 * high and a person raises the emergency themselves. Anything not a finding starts low, because
 * raising a job at all was a deliberate act.
 */
export function suggestedPriority(condition: string | null | undefined): MaintenancePriority {
  const info = conditionInfo(condition);
  if (info.value === "damaged" || info.value === "missing") return "high";
  if (info.value === "poor") return "normal";
  return "low";
}

/**
 * The trade a finding belongs to, where the area already names one.
 *
 * Only the unambiguous ones. Guessing that a kitchen fault is an appliance fault would file half
 * the plumbing in the building under the wrong heading, and a category that is wrong is worse than
 * one that is empty — the empty one gets chosen, the wrong one gets kept.
 */
const AREA_CATEGORY: Record<string, string> = {
  electrical: "Electrical",
  plumbing: "Plumbing",
  security: "Security",
};

export function suggestedCategory(area: string | null | undefined): string | null {
  return AREA_CATEGORY[(area ?? "").trim().toLowerCase()] ?? null;
}

export interface FindingDraftInput {
  area: string;
  item: string;
  condition?: string | null;
  note?: string | null;
  kind: InspectionKind | string;
  /** The day the inspection was carried out, or is due. `YYYY-MM-DD` — never a `Date`. */
  inspectedOn?: PlainDate | null;
  /** Where it is, in words a maintenance list can show: "Adeola Court · Flat 3B". */
  where?: string | null;
  /** The inspection's own reference, so the job says where it came from. */
  reference?: string | null;
}

export interface MaintenanceDraft {
  title: string;
  description: string;
  category: string | null;
  priority: MaintenancePriority;
}

/**
 * What a maintenance job raised from a finding says.
 *
 * **The whole point is that nobody retypes it.** A manager reading the maintenance list a month
 * later needs to know what was found, where, when, and that it came from an inspection rather than
 * from a tenant's telephone call — so all four go into the job, composed here rather than in the
 * screen, so that a job raised from anywhere says the same thing.
 */
export function maintenanceDraftFrom(input: FindingDraftInput): MaintenanceDraft {
  const condition = conditionInfo(input.condition);
  // Titles sit in a table column. Past about eighty characters the column wraps to three lines and
  // the list stops being scannable.
  const title = `${input.item} — ${condition.label.toLowerCase()}`.slice(0, 80);

  const lines = [
    `Found during the ${inspectionKindLabel(input.kind).toLowerCase()} inspection${
      input.reference ? ` ${input.reference}` : ""
    }${input.inspectedOn ? ` on ${input.inspectedOn}` : ""}.`,
    `${input.area}: ${input.item} — ${condition.label.toLowerCase()}.`,
  ];
  if (input.where) lines.push(`Where: ${input.where}.`);
  if (input.note?.trim()) lines.push(`Inspector's note: ${input.note.trim()}`);

  return {
    title,
    description: lines.join("\n"),
    category: suggestedCategory(input.area),
    priority: suggestedPriority(input.condition),
  };
}

/* ------------------------------------------------------------------ when it is due */

export type DueState = "overdue" | "due_today" | "upcoming" | "undated" | "done" | "cancelled";

/**
 * Where an inspection stands against today.
 *
 * Dates that mean a day are compared as strings, which is exactly why they are stored as
 * `YYYY-MM-DD` — the comparison is a string comparison and there is no timezone in it to get
 * wrong. `today` is the agency's today, not the server's.
 */
export function dueState(
  status: string | null | undefined,
  scheduledFor: PlainDate | null | undefined,
  today: PlainDate,
): DueState {
  if (status === "completed") return "done";
  if (status === "cancelled") return "cancelled";
  if (!scheduledFor) return "undated";
  if (scheduledFor < today) return "overdue";
  if (scheduledFor === today) return "due_today";
  return "upcoming";
}

export function dueLabel(state: DueState): string {
  switch (state) {
    case "overdue":
      return "Overdue";
    case "due_today":
      return "Due today";
    case "upcoming":
      return "Upcoming";
    case "undated":
      return "No date set";
    case "done":
      return "Completed";
    case "cancelled":
      return "Cancelled";
  }
}
